/* eslint-disable react/prop-types */
/* eslint-disable import/order */

// `@fullcalendar/react` MUST be imported before any other @fullcalendar
// package because its side-effect import installs the React vdom backend
// into `globalThis.FullCalendarVDom`. If `@fullcalendar/core` (which installs
// a preact backend) loads first, `@fullcalendar/react` warns "already loaded"
// and silently does nothing — leaving CalendarRoot using preact's
// createContext, which React rejects ("invalid contextType ... keys {__c, __,
// Consumer, Provider}") and which then crashes on string refs in StrictMode.
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import React from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { DayHeaderContent } from './components/DayHeaderContent'
import { ScheduleCard } from './components/ScheduleCard'
// import { events } from './mocks/events'
import { Container } from './styles'
import { fetchInventorySchedules } from 'api'
import constants from 'app/constants'
import { eventEmitter, showToast } from 'app/helpers'
import { useIsMobile } from 'app/hooks/useIsMobile'
import { isEmpty } from 'lodash'
import { composeBatchDetails } from '../../transformers'

dayjs.extend(customParseFormat)

const Schedule = ({
  selectedBatchType,
  selectedLevel = '',
  selectedGrades = [],
  selectedCategories = [],
  selectedCentres = [],
  selectedClosedBatch = 'hide_closed'
}) => {
  const fcRef = React.useRef(null)
  const isMobile = useIsMobile()

  return (
    <Container>
      <FullCalendar
        ref={fcRef}
        plugins={[dayGridPlugin]}
        // Mobile: single-day view. The 7-column week grid is unreadable
        // below ~600px — each column collapses to ~50px which can't fit a
        // batch name. dayGridDay gives the day full viewport width, user
        // taps prev/next to navigate.
        initialView={isMobile ? 'dayGridDay' : 'dayGridWeek'}
        firstDay="1" // monday
        headerToolbar={{ left: 'prev next title', center: '', right: '' }}
        titleFormat={{
          day: 'numeric',
          month: 'short'
        }}
        eventContent={(props) => <ScheduleCard {...props} />}
        dayHeaderContent={DayHeaderContent}
        events={async (dateRange, successCB, failureCB) => {
          try {
            if (isEmpty(selectedCentres) || isEmpty(selectedCategories))
              return successCB([])

            showToast('Fetching Schedule...', constants.INFO)
            const { results } = await fetchInventorySchedules({
              queries: {
                limit: 9999,
                centre_uuid:
                  // TODO - uncomment after batch api supports multiple centre uuids
                  // selectedCentres.map((item) => item.value).join(',') ||
                  selectedCentres.value || undefined,
                standards_uuid:
                  selectedGrades.map((item) => item.value).join(',') ||
                  undefined,
                subjects_uuid:
                  selectedCategories.map((item) => item.value).join(',') ||
                  undefined,
                type: selectedBatchType,
                level: selectedLevel,
                date_gte: dayjs(dateRange.start).format('YYYY-MM-DD'),
                date_lte: dayjs(dateRange.end)
                  .subtract(1, 'day')
                  .format('YYYY-MM-DD')
              }
            })

            // const filteredResults = (results || []).filter((batch) => {
            //   const subjects = batch?.course?.subjects || []
            //   const hasToddlerClub = subjects.some(
            //     (subject) => subject?.subject_category?.name === 'Toddler Club'
            //   )
            //   return hasToddlerClub && batch?.type === 'event' ? false : true
            // })

            let filteredResults = results || []

            // Apply closed batch filter if not 'school' or 'care' batch types
            if (
              !['school', 'care'].includes(selectedBatchType) &&
              selectedClosedBatch === 'hide_closed'
            ) {
              filteredResults = filteredResults.filter((batch) => {
                return batch.enrolment_capacity !== 0
              })
            }

            const schedule = []
            const dateStart = dayjs(dateRange.start).format('YYYY-MM-DD')

            filteredResults.forEach((batch) => {
              const { name, uuid, cadence, start_date, end_date } = batch
              cadence.forEach((cad) => {
                const startTime = dayjs(cad.start_time, 'HH-mm').format(
                  'hh:mm:ss a'
                )
                const startDateTime = dayjs(
                  `${dateStart} ${startTime}`,
                  'YYYY-MM-DD hh:mm:ss a',
                  false
                ).add(cad.weekday, 'day')

                let isWithinRange =
                  startDateTime.isSameOrAfter(start_date) &&
                  (startDateTime.isSame(end_date, 'day') ||
                    startDateTime.isBefore(end_date))

                let isDisabled = false

                if (selectedBatchType === 'explore') {
                  isDisabled =
                    startDateTime.isSameOrAfter(start_date) &&
                    (startDateTime.isSame(end_date, 'day') ||
                      startDateTime.isBefore(end_date)) &&
                    startDateTime.isSame(dayjs(), 'day') &&
                    startDateTime.isBefore(dayjs(), 'minute')
                }

                if (!isWithinRange) return

                const { alternateBatches: timings } = composeBatchDetails(batch)

                schedule.push({
                  id: uuid,
                  title: name,
                  start: startDateTime.toDate(),
                  extendedProps: {
                    ...batch,
                    isDisabled: isDisabled,
                    timings
                  }
                })
              })
            })

            if (isEmpty(schedule)) {
              showToast('Uh.oh! No batches found', constants.ERROR)
            }
            successCB(schedule)
            eventEmitter.emit(constants.BATCH_RESELECTION)
          } catch (e) {
            failureCB([])
            showToast('Oh.oh! Something went wrong', constants.ERROR)
          }
        }}
      />
    </Container>
  )
}

export default Schedule
